import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo, useCallback } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CommonTable from "../../../common_components/CommonTable";
import CommonChip from "../../../common_components/CommonChip";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import ExpandedRow from "./ExpandedRow";
import { getReceiverLabel, resolveModuleDisplayName, } from "../utils";
const OverflowChip = ({ label, items }) => (_jsx(HoverTooltip, { title: _jsx(Box, { className: "email-configuration-tooltip-card", sx: { maxHeight: "250px", overflowY: "auto" }, children: items.map((item, idx) => (_jsx(Box, { className: idx !== items.length - 1
                ? "email-configuration-tooltip-item email-configuration-tooltip-item-divider"
                : "email-configuration-tooltip-item", children: item }, idx))) }), arrow: true, placement: "top", PopperProps: {
        sx: {
            zIndex: 1400,
            "& .MuiTooltip-tooltip": { background: "transparent", padding: 0 },
            "& .MuiTooltip-arrow": { color: "black" },
        },
    }, children: _jsx("span", { className: "email-configuration-overflow-chip", children: label }) }));
const EmailConfigList = ({ configurations, moduleNameMap, loading, isFirstLoad = false, searchText, onEditModule, onDeleteModule, onNotesModule, sortConfig, onSortChange, columnOrder, onColumnOrderChange, pinnedColumns, onColumnPinningChange, hiddenColumns, onColumnVisibilityChange, columnVisibilityAnchor, onColumnVisibilityClose, }) => {
    const [expandedRows, setExpandedRows] = useState(new Set());
    // Transform configurations for CommonTable rows
    const rows = useMemo(() => {
        return configurations.map((config) => ({
            id: config._id,
            ...config,
            moduleName: resolveModuleDisplayName(config, moduleNameMap),
        }));
    }, [configurations, moduleNameMap]);
    const handleExpandToggle = useCallback((rowId) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(rowId)) {
                next.delete(rowId);
            }
            else {
                next.add(rowId);
            }
            return next;
        });
    }, []);
    const columns = [
        {
            id: "module",
            label: "Module",
            width: "320px",
            align: "left",
            sortable: true,
        },
        {
            id: "subject",
            label: "Subject",
            width: "220px",
            align: "left",
            sortable: true,
        },
        {
            id: "receiver",
            label: "Receiver",
            width: "150px",
            align: "left",
            sortable: true,
        },
        {
            id: "defaultRecipients",
            label: "Default Recipient",
            width: "220px",
            align: "left",
            sortable: true,
        },
        {
            id: "additionalRecipients",
            label: "Additional Recipient",
            width: "240px",
            align: "left",
            sortable: false,
            showColumnActions: false,
        },
        {
            id: "actions",
            label: "Actions",
            width: "140px",
            align: "center",
            sortable: false,
            showColumnActions: false,
            draggable: false,
        },
    ];
    const renderCell = (row, column) => {
        switch (column.id) {
            case "module": {
                const hasModuleName = !!row.moduleName;
                const hasNotificationKey = !!row.notification_key;
                if (!hasModuleName && !hasNotificationKey) {
                    return _jsx(Typography, { className: "email-configuration-na-text", children: "N/A" });
                }
                return (_jsxs(Box, { className: "email-configuration-module-cell", children: [hasModuleName && (_jsx(Typography, { className: "email-configuration-module-name", children: row.moduleName })), hasNotificationKey && (_jsx(HoverTooltip, { title: _jsx(Box, { className: "email-configuration-tooltip-card", children: _jsx(Box, { className: "email-configuration-tooltip-text", children: row.notification_key }) }), arrow: true, placement: "top", PopperProps: {
                                sx: {
                                    zIndex: 1400,
                                    "& .MuiTooltip-tooltip": { background: "transparent", padding: 0 },
                                    "& .MuiTooltip-arrow": { color: "black" },
                                },
                            }, children: _jsx(Typography, { className: "email-configuration-notif-key", children: row.notification_key }) }))] }));
            }
            case "subject":
                const subjectText = row.subject || '';
                return subjectText ? (_jsx(HoverTooltip, { title: _jsx(Box, { className: "email-configuration-tooltip-card", children: _jsx(Box, { className: "email-configuration-tooltip-text", children: subjectText }) }), arrow: true, placement: "top", PopperProps: {
                        sx: {
                            zIndex: 1400,
                            "& .MuiTooltip-tooltip": { background: "transparent", padding: 0 },
                            "& .MuiTooltip-arrow": { color: "black" },
                        },
                    }, children: _jsx(Typography, { className: "email-configuration-subject-text", children: subjectText }) })) : (_jsx(Typography, { className: "email-configuration-na-text", children: "N/A" }));
            case "receiver": {
                const receiverLabel = getReceiverLabel(row.default_recipient || []);
                const isNA = receiverLabel === 'N/A';
                return (_jsx(Typography, { className: isNA ? "email-configuration-na-text" : "email-configuration-receiver-text", children: receiverLabel }));
            }
            case "defaultRecipients":
                const recipients = row.default_recipient || [];
                return (_jsxs(Box, { className: "email-configuration-chips-row", children: [recipients.length > 0 && (_jsxs(_Fragment, { children: [_jsx(CommonChip, { label: recipients[0], renderType: "tag", index: 0, sx: { fontSize: "14px", fontWeight: 500 } }, recipients[0]), recipients.length > 1 && (_jsx(OverflowChip, { label: `+${recipients.length - 1}`, items: recipients.slice(1) }))] })), recipients.length === 0 && (_jsx(Typography, { className: "email-configuration-na-text", children: "N/A" }))] }));
            case "additionalRecipients":
                const extraAdditional = row.additional_user_ids?.slice(1) || [];
                return (_jsxs(Box, { className: "email-configuration-chips-row", children: [row.additional_user_ids?.length > 0 && (_jsxs(_Fragment, { children: [_jsx(CommonChip, { label: `${row.additional_user_ids[0].first_name} ${row.additional_user_ids[0].last_name}`, renderType: "tag", index: 0, sx: { fontSize: "14px", fontWeight: 500 } }, row.additional_user_ids[0]._id), row.additional_user_ids.length > 1 && (_jsx(OverflowChip, { label: `+${row.additional_user_ids.length - 1}`, items: extraAdditional.map((u) => `${u.first_name} ${u.last_name}`) }))] })), (!row.additional_user_ids || row.additional_user_ids.length === 0) && (_jsx(Typography, { className: "email-configuration-na-text", children: "N/A" }))] }));
            case "actions":
                return (_jsxs(Box, { className: "emailconfig-table-actions-cell", children: [_jsx(HoverTooltip, { title: "Edit Configuration", children: _jsx(IconButton, { className: "emailconfig-table-action-button", size: "small", onClick: (e) => {
                                    e.stopPropagation();
                                    onEditModule(row);
                                }, children: _jsx("img", { src: "/assets/edit.svg", alt: "edit" }) }) }), _jsx(HoverTooltip, { title: "Notes", children: _jsx(IconButton, { className: "emailconfig-table-action-button", size: "small", onClick: (e) => {
                                    e.stopPropagation();
                                    onNotesModule(row);
                                }, children: _jsxs("span", { className: "emailconfig-note-icon", children: [_jsx("img", { src: "/assets/note-gray.svg", alt: "notes" }), row.notes?.trim() && _jsx("span", { className: "emailconfig-note-dot" })] }) }) }), _jsx(HoverTooltip, { title: "Delete Configuration", children: _jsx(IconButton, { className: "emailconfig-table-action-button delete", size: "small", onClick: (e) => {
                                    e.stopPropagation();
                                    onDeleteModule(row);
                                }, children: _jsx("img", { src: "/assets/delete.svg", alt: "delete" }) }) })] }));
            default:
                return null;
        }
    };
    const renderExpandedContent = (row) => {
        return _jsx(ExpandedRow, { configuration: row });
    };
    return (_jsx(CommonTable, { columns: columns, rows: rows, renderCell: renderCell, renderExpandedContent: renderExpandedContent, expandTrigger: "icon", controlledExpandedRows: expandedRows, onExpandToggle: handleExpandToggle, type: "email-config-table", emptyMessage: searchText ? 'No configurations found matching your search' : 'No email configurations found', isLoading: loading, isFirstLoad: isFirstLoad, sortConfig: sortConfig, onSortChange: onSortChange, enableColumnActions: true, enableColumnReorder: true, columnOrder: columnOrder, onColumnOrderChange: onColumnOrderChange, enableColumnPinning: true, pinnedColumns: pinnedColumns, onColumnPinningChange: onColumnPinningChange, columnVisibilityConfig: {
            enable: true,
            columns: ["module", "subject", "receiver", "defaultRecipients", "additionalRecipients", "actions"],
            enableReorder: true,
        }, hiddenColumns: hiddenColumns, onColumnVisibilityChange: onColumnVisibilityChange, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: onColumnVisibilityClose, stickyHeader: true, maxHeight: "100%" }));
};
export default EmailConfigList;
